import { Head, Link } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeadCell,
  TableCell,
} from "@/components/ui/table"

type UserRow = {
  id: number
  name: string
  email: string
  admin: boolean
  status: "active" | "pending" | "deactivated"
  created_at: string
}

const STATUS_TONES = {
  active: "neutral",
  pending: "signal",
  deactivated: "muted",
} as const

const STATUS_LABELS = {
  active: "Active",
  pending: "Invite pending",
  deactivated: "Deactivated",
} as const

export default function AdminUsersIndex({ users }: { users: UserRow[] }) {
  return (
    <>
      <Head title="Users">
        <meta name="description" content="Manage users in the admin area." />
        <meta property="og:title" content="Users" />
        <meta property="og:description" content="Manage users in the admin area." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>Users</h1>
              <p className="mt-1">
                {users.length} {users.length === 1 ? "user" : "users"} in this app.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button asChild>
                <Link href="/admin/users/new">Invite user</Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <Table>
            <TableHead>
              <tr>
                <TableHeadCell>Name</TableHeadCell>
                <TableHeadCell>Email</TableHeadCell>
                <TableHeadCell>Role</TableHeadCell>
                <TableHeadCell>Status</TableHeadCell>
                <TableHeadCell>Joined</TableHeadCell>
              </tr>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} href={`/admin/users/${user.id}`}>
                  <TableCell>
                    <Link
                      href={`/admin/users/${user.id}`}
                      className="font-medium text-ink-display no-underline hover:underline"
                    >
                      {user.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-ink-muted">{user.email}</TableCell>
                  <TableCell>
                    {user.admin ? <Badge tone="accent">Admin</Badge> : "Member"}
                  </TableCell>
                  <TableCell>
                    <Badge tone={STATUS_TONES[user.status]}>
                      {STATUS_LABELS[user.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-ink-muted">
                    {formatDate(user.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </AdminShell>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
